import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft,
  Skull,
  Scale,
  ArrowRightLeft,
  Truck,
  Wheat,
  Syringe,
  Receipt,
  Tag,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { QuickHubAction } from '@/types/navigation';
import { PigArrivalModal } from '@/components/cycles/PigArrivalModal';
import { MortalityLogModal } from '@/components/cycles/MortalityLogModal';
import { PigTransferModal } from '@/components/cycles/PigTransferModal';
import { BulkFeedInvoiceModal } from '@/components/feed/BulkFeedInvoiceModal';
import { FeedTransferModal } from '@/components/feed/FeedTransferModal';
import { HealthEventModal } from '@/components/health/HealthEventModal';
import { CycleExpenseModal } from '@/components/expenses/CycleExpenseModal';
import { PigSaleModal } from '@/components/sales/PigSaleModal';

interface RecordActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RecordActivityModal: React.FC<RecordActivityModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const [activeModal, setActiveModal] = useState<string | null>(null);

  const actions: QuickHubAction[] = [
    {
      id: 'arrivals',
      title: 'Pigs Arrived',
      subtitle: 'Record incoming pig placement & paper slip',
      icon: ArrowDownLeft,
      iconBgColor: 'bg-emerald-100',
      iconTextColor: 'text-emerald-800',
      category: 'animals',
    },
    {
      id: 'mortality',
      title: 'Mortality Log',
      subtitle: 'Log animal loss with cause & carcass photo',
      icon: Skull,
      iconBgColor: 'bg-red-100',
      iconTextColor: 'text-red-700',
      category: 'animals',
    },
    {
      id: 'transfers',
      title: 'Pig Transfer',
      subtitle: 'Move pigs between pens or cycles',
      icon: ArrowRightLeft,
      iconBgColor: 'bg-purple-100',
      iconTextColor: 'text-purple-800',
      category: 'animals',
    },
    {
      id: 'bulk_feed',
      title: 'Bulk Feed Invoice',
      subtitle: 'Allocate multi-line supplier invoice to cycles',
      icon: Truck,
      iconBgColor: 'bg-amber-100',
      iconTextColor: 'text-amber-800',
      category: 'feed',
    },
    {
      id: 'feed_transfer',
      title: 'Feed Transfer',
      subtitle: 'Move feed between cycles with cost preservation',
      icon: Wheat,
      iconBgColor: 'bg-purple-100',
      iconTextColor: 'text-purple-800',
      category: 'feed',
    },
    {
      id: 'health',
      title: 'Treatment / ASF Health',
      subtitle: 'Vaccines, medicines, or diagnostic lab tests',
      icon: Syringe,
      iconBgColor: 'bg-teal-100',
      iconTextColor: 'text-teal-800',
      category: 'health',
    },
    {
      id: 'expense',
      title: 'Log Farm Expense',
      subtitle: 'Trucking, hauling permits, bedding, repairs',
      icon: Receipt,
      iconBgColor: 'bg-slate-100',
      iconTextColor: 'text-slate-800',
      category: 'finance',
    },
    {
      id: 'sales',
      title: 'Pig Sale & Scale Ticket',
      subtitle: 'Open rapid scale weighing grid for buyer checkout',
      icon: Tag,
      iconBgColor: 'bg-emerald-100',
      iconTextColor: 'text-emerald-900',
      actionRoute: '/sales',
      category: 'sales',
    },
  ];

  const handleSelectAction = (action: QuickHubAction) => {
    if (
      action.id === 'arrivals' ||
      action.id === 'mortality' ||
      action.id === 'transfers' ||
      action.id === 'bulk_feed' ||
      action.id === 'feed_transfer' ||
      action.id === 'health' ||
      action.id === 'expense' ||
      action.id === 'sales'
    ) {
      setActiveModal(action.id);
    } else if (action.actionRoute) {
      navigate(action.actionRoute);
      onClose();
    }
  };

  const handleModalSuccess = () => {
    setActiveModal(null);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen && !activeModal} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto p-4 sm:p-6 bg-white">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-bold text-slate-900">Record Operational Activity</DialogTitle>
            <p className="text-xs text-slate-500">
              Select an activity below to record direct field operations.
            </p>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => handleSelectAction(action)}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/30 transition-all text-left group"
                >
                  <div className={`p-2 rounded-md ${action.iconBgColor} ${action.iconTextColor} shrink-0 mt-0.5`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 group-hover:text-emerald-900">
                      {action.title}
                    </h4>
                    <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                      {action.subtitle}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {activeModal === 'arrivals' && (
        <PigArrivalModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'mortality' && (
        <MortalityLogModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'transfers' && (
        <PigTransferModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'bulk_feed' && (
        <BulkFeedInvoiceModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'feed_transfer' && (
        <FeedTransferModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'health' && (
        <HealthEventModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'expense' && (
        <CycleExpenseModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'sales' && (
        <PigSaleModal
          isOpen={true}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};
